import { FRAIS_CAT } from '../constants/accounting';
import { paiementEtat } from './paiements-etat';
import { dansPeriode, litDate } from './periodes';
import { tvaDeLaDepense } from './vat';

/**
 * Une dépense payée pour plusieurs mois — l'assurance payée en janvier pour
 * l'année, l'AVS au semestre — et « répartie sur les mois couverts ».
 *
 * <p>Le paiement reste inscrit une seule fois, à sa date, avec sa pièce et sa
 * TVA : la banque, la TVA récupérable et la position TVA du serveur le voient
 * tel qu'il a été payé. Seule sa charge se répartit, sur l'écran du bénéfice :
 * 1'200.– payés en janvier pour l'année comptent 100.– par mois, de janvier
 * à décembre, au lieu d'un mois de janvier en perte et de onze mois trop
 * beaux. Un paiement à cheval sur deux années se partage entre elles.
 *
 * <p>Les paiements à l'État (TVA, AVS, impôts) ne sont jamais répartis : ce ne
 * sont pas des charges, ils remplissent « Déjà payé » à leur date.
 */

/**
 * Les mois sur lesquels la charge d'une dépense se répartit.
 *
 * @param {object} [x] dépense
 * @returns {number} 1 si elle compte en entier le mois où elle est payée
 */
export function moisCouverts(x) {
  const n = Math.round(+x?.moisCouverts || 0);
  return n > 1 && !paiementEtat(x?.type) ? Math.min(n, 12) : 1;
}

/**
 * Découpe un montant en parts égales, au centime, dont la somme redonne le
 * montant exact : 1'000.– sur douze mois font onze fois 83.33 et une fois
 * 83.37 — l'année retombe sur 1'000.–, pas sur 999.96.
 *
 * @param {number} montant montant à découper
 * @param {number} n nombre de parts
 * @returns {number[]} les parts, dans l'ordre des mois
 */
export function enParts(montant, n) {
  const centimes = Math.round((+montant || 0) * 100);
  const parts = Math.max(1, Math.round(+n || 1));
  return Array.from(
    { length: parts },
    (_, i) => (Math.round((centimes * (i + 1)) / parts) - Math.round((centimes * i) / parts)) / 100,
  );
}

/**
 * Les dates des mois couverts par une dépense répartie : le jour du paiement,
 * puis le même jour les mois suivants (le dernier d'un mois plus court).
 *
 * @param {object} x dépense
 * @param {Date} [maintenant] date de référence, pour relire une date sans année
 * @returns {string[]|null} les dates `JJ.MM.AAAA`, ou null si elle n'est pas
 *   répartie (ou sa date ne se lit pas)
 */
export function datesCouvertes(x, maintenant = new Date()) {
  const n = moisCouverts(x);
  if (n <= 1) return null;
  const lue = litDate(x.dateCompta || x.date, maintenant);
  if (!lue) return null;
  const jour = lue.getDate();
  const depart = lue.getFullYear() * 12 + lue.getMonth();
  return Array.from({ length: n }, (_, i) => {
    const annee = Math.floor((depart + i) / 12);
    const mois = ((depart + i) % 12) + 1;
    const dernier = new Date(annee, mois, 0).getDate();
    return (
      String(Math.min(jour, dernier)).padStart(2, '0') +
      '.' +
      String(mois).padStart(2, '0') +
      '.' +
      annee
    );
  });
}

/**
 * La part d'une dépense qui tombe dans une période : la dépense entière si
 * elle y est payée, ou, répartie, ses mois couverts compris dans la période.
 *
 * @param {object} x dépense
 * @param {Date} debut borne comprise
 * @param {Date} fin borne comprise
 * @param {Date} [maintenant] date de référence
 * @returns {number} le montant compté dans la période, TVA comprise
 */
export function partDansLaPeriode(x, debut, fin, maintenant = new Date()) {
  const dates = datesCouvertes(x, maintenant);
  if (!dates) {
    return dansPeriode(x?.dateCompta || x?.date, debut, fin, maintenant) ? +x?.montant || 0 : 0;
  }
  const parts = enParts(x.montant, dates.length);
  return dates.reduce((s, d, i) => s + (dansPeriode(d, debut, fin, maintenant) ? parts[i] : 0), 0);
}

/**
 * Les dépenses telles que les charges les comptent : une dépense répartie
 * devient une part par mois couvert, montant et TVA découpés au centime ; les
 * autres restent telles quelles.
 *
 * <p>Pour les écrans du bénéfice et des charges seulement : la TVA
 * récupérable, la banque et la liste des dépenses lisent les paiements tels
 * qu'ils ont été faits. Chaque part porte la TVA de son paiement, calculée au
 * taux du jour où il a été payé, même si le mois couvert tombe après un
 * changement de taux.
 *
 * @param {object[]} [frais] dépenses
 * @param {object} [soc] entreprise, pour le taux de TVA des dépenses
 * @param {Date} [maintenant] date de référence
 * @returns {object[]} les dépenses, les réparties remplacées par leurs parts
 *   (`partDe` : l'identifiant du paiement, `mois` : le rang du mois, `sur` :
 *   le nombre de mois couverts)
 */
export function chargesReparties(frais, soc, maintenant = new Date()) {
  return (frais || []).flatMap((x) => {
    const dates = x ? datesCouvertes(x, maintenant) : null;
    if (!dates) return [x];
    const montants = enParts(x.montant, dates.length);
    const tva = enParts(tvaDeLaDepense(x, FRAIS_CAT, soc), dates.length);
    return dates.map((date, i) => ({
      ...x,
      date,
      dateCompta: null,
      montant: montants[i],
      montantTva: tva[i],
      moisCouverts: null,
      // Sans identifiant (pas encore enregistrée), la dépense elle-même.
      partDe: x.id ?? x,
      mois: i + 1,
      sur: dates.length,
    }));
  });
}

/**
 * Regroupe, pour l'affichage, les parts d'un même paiement réparti : une
 * ligne « 3 mois sur 12 » plutôt que trois lignes de la même somme.
 *
 * @param {object[]} [lignes] dépenses de {@link chargesReparties}, d'une période
 * @returns {object[]} les dépenses, une ligne par paiement (`nbMois` : ses
 *   mois comptés dans la période)
 */
export function regroupeLesParts(lignes) {
  const sortie = [];
  const parPaiement = new Map();
  (lignes || []).forEach((x) => {
    if (!x || x.partDe == null) {
      sortie.push(x);
      return;
    }
    const deja = parPaiement.get(x.partDe);
    if (deja) {
      deja.montant = Math.round((deja.montant + (+x.montant || 0)) * 100) / 100;
      deja.montantTva = Math.round((deja.montantTva + (+x.montantTva || 0)) * 100) / 100;
      deja.nbMois += 1;
      return;
    }
    const ligne = { ...x, montant: +x.montant || 0, montantTva: +x.montantTva || 0, nbMois: 1 };
    parPaiement.set(x.partDe, ligne);
    sortie.push(ligne);
  });
  return sortie;
}
